import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { PropsOf } from 'utils/reactHelpers';

import { FolderId } from 'modules/domains/foldering';
import { JourneySubfolder } from 'modules/domains/journey';
import { pxToRem } from 'utils/convert';

import { Workflow, WorkflowCreatorOrModifierUser, WorkflowId } from '../../types';
import { DEFAULT_SKELETON_ROW_COUNT } from '../../constants';

import { HeaderRow } from './HeaderRow';
import { WorkflowRow } from './WorkflowRow';
import { WorkflowFolderRow } from './WorkflowFolderRow';
import { WorkflowSkeletonRow } from './WorkflowSkeletonRow';
import { WorkflowFolderSkeletonRow } from './WorkflowFolderSkeletonRow';

type WorkflowsTableProps = Omit<PropsOf<typeof HeaderRow>, 'isGroupSelectionDisabled'> & {
  workflows: Workflow[];
  creatorAndModifierUsersById: Record<string, WorkflowCreatorOrModifierUser>;
  onArchiveWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onUnarchiveWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onCopyWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onDeleteWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onEnableWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onDisableWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onMoveWorkflowOptionClick: (workflowId: WorkflowId) => void;
  onDeleteWorkflowFolderOptionClick: (folderId: FolderId) => void;
  onMoveWorkflowFolderOptionClick: (folderId: FolderId) => void;
  onRenameWorkflowFolderOptionClick: (folderId: FolderId) => void;
  onCreateWorkflowFolderOptionClick: (folderId: FolderId) => void;
  folders: JourneySubfolder[];
  isLoading: boolean;
};

const useSkeletonRows = (
  isLoading: boolean,
  workflows: Workflow[],
  folders: JourneySubfolder[],
) => {
  const [workflowCountBeforeLoading, setWorkflowCountBeforeLoading] = useState(workflows.length);
  const [folderCountBeforeLoading, setFolderCountBeforeLoading] = useState(folders.length);
  useEffect(() => {
    if (!isLoading) {
      setWorkflowCountBeforeLoading(workflows.length);
      setFolderCountBeforeLoading(folders.length);
    }
  }, [isLoading, workflows.length, folders.length]);
  return useMemo(() => {
    const workflowSkeletonCount =
      workflowCountBeforeLoading || (folderCountBeforeLoading ? 0 : DEFAULT_SKELETON_ROW_COUNT);
    const folderSkeletons = new Array(folderCountBeforeLoading)
      .fill(null)
      .map((_, i) => <WorkflowFolderSkeletonRow key={`folder-skeleton-${i}`} index={i} />);
    const workflowSkeletons = new Array(workflowSkeletonCount)
      .fill(null)
      .map((_, i) => <WorkflowSkeletonRow key={`workflow-skeleton-${i}`} />);

    return [...folderSkeletons, ...workflowSkeletons];
  }, [workflowCountBeforeLoading, folderCountBeforeLoading]);
};

const StyledWorkflowsTable = styled.div`
  min-width: ${pxToRem(1000)};
`;

export const WorkflowsTable = ({
  workflows,
  creatorAndModifierUsersById,
  onArchiveWorkflowOptionClick,
  onUnarchiveWorkflowOptionClick,
  onCopyWorkflowOptionClick,
  folders,
  onDeleteWorkflowFolderOptionClick,
  onMoveWorkflowFolderOptionClick,
  onDeleteWorkflowOptionClick,
  onEnableWorkflowOptionClick,
  onDisableWorkflowOptionClick,
  onRenameWorkflowFolderOptionClick,
  onCreateWorkflowFolderOptionClick,
  onMoveWorkflowOptionClick,
  isLoading,
  ...headerRowProps
}: WorkflowsTableProps) => {
  const workflowRows = useMemo(
    () =>
      workflows.map((workflow) => (
        <WorkflowRow
          workflow={workflow}
          creatorAndModifierUsersById={creatorAndModifierUsersById}
          key={`${workflow.id}-${workflow.workflowType}`}
          onArchiveOptionClick={onArchiveWorkflowOptionClick}
          onUnarchiveOptionClick={onUnarchiveWorkflowOptionClick}
          onCopyOptionClick={onCopyWorkflowOptionClick}
          onDeleteOptionClick={onDeleteWorkflowOptionClick}
          onEnableOptionClick={onEnableWorkflowOptionClick}
          onDisableOptionClick={onDisableWorkflowOptionClick}
          onMoveOptionClick={onMoveWorkflowOptionClick}
        />
      )),
    [
      workflows,
      creatorAndModifierUsersById,
      onArchiveWorkflowOptionClick,
      onUnarchiveWorkflowOptionClick,
      onCopyWorkflowOptionClick,
      onEnableWorkflowOptionClick,
      onDisableWorkflowOptionClick,
    ],
  );

  const folderRows = useMemo(
    () =>
      folders.map((folder) => (
        <WorkflowFolderRow
          folder={folder}
          key={folder.id}
          onDeleteOptionClick={onDeleteWorkflowFolderOptionClick}
          onMoveOptionClick={onMoveWorkflowFolderOptionClick}
          onRenameOptionClick={onRenameWorkflowFolderOptionClick}
          onCreateOptionClick={onCreateWorkflowFolderOptionClick}
        />
      )),
    [
      folders,
      onDeleteWorkflowFolderOptionClick,
      onMoveWorkflowFolderOptionClick,
      onRenameWorkflowFolderOptionClick,
      onCreateWorkflowFolderOptionClick,
    ],
  );
  const skeletonRows = useSkeletonRows(isLoading, workflows, folders);

  return (
    <StyledWorkflowsTable data-test="workflows-table">
      <HeaderRow isGroupSelectionDisabled={isLoading} {...headerRowProps} />
      {isLoading ? (
        skeletonRows
      ) : (
        <>
          {folderRows}
          {workflowRows}
        </>
      )}
    </StyledWorkflowsTable>
  );
};
